import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { backendErrorMessage, logWebSocketError, readJsonResponse } from "../apiDiagnostics";
import { API_URL, WS_URL } from "../config";
import PageShell from "../components/PageShell";

function TeacherView() {
  const { id, secret } = useParams();
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("Verbinde...");
  const [deleted, setDeleted] = useState(false);

  async function loadTeacherView() {
    const url = `${API_URL}/api/teacher/${id}/${secret}`;
    try {
      const response = await fetch(url);
      const data = await readJsonResponse(response, "Lehreransicht laden", url);
      if (response.ok) {
        setList(data);
        setError("");
      } else {
        setError(data.error || "Lehreransicht nicht verfügbar.");
      }
    } catch (err) {
      setError(backendErrorMessage("Lehreransicht laden", url, err));
    }
  }

  useEffect(() => {
    loadTeacherView();

    let ws;
    let reconnectTimer;

    function connectWebSocket() {
      const wsUrl = `${WS_URL}?listId=${id}`;
      ws = new WebSocket(wsUrl);

      ws.onopen = () => setConnectionStatus("Live verbunden");
      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "LIST_UPDATED") setList(data.list);
        } catch (err) {
          logWebSocketError(wsUrl, err);
        }
      };
      ws.onerror = event => {
        logWebSocketError(wsUrl, event);
        setConnectionStatus("Live-Verbindung gestört");
      };
      ws.onclose = () => {
        setConnectionStatus("Verbindung wird neu aufgebaut...");
        reconnectTimer = setTimeout(connectWebSocket, 3000);
      };
    }

    connectWebSocket();

    return () => {
      clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [id, secret]);

  async function closeList() {
    const url = `${API_URL}/api/teacher/${id}/${secret}/close`;
    try {
      const response = await fetch(url, { method: "POST" });
      const data = await readJsonResponse(response, "Liste schließen", url);
      if (response.ok) setList(data);
      else setError(data.error || "Ordnungsliste konnte nicht geschlossen werden.");
    } catch (err) {
      setError(backendErrorMessage("Liste schließen", url, err));
    }
  }

  async function reopenList() {
    const url = `${API_URL}/api/teacher/${id}/${secret}/reopen`;
    try {
      const response = await fetch(url, { method: "POST" });
      const data = await readJsonResponse(response, "Liste wieder öffnen", url);
      if (response.ok) setList(data);
      else setError(data.error || "Ordnungsliste konnte nicht wieder geöffnet werden.");
    } catch (err) {
      setError(backendErrorMessage("Liste wieder öffnen", url, err));
    }
  }

  async function deleteList() {
    const confirmed = window.confirm("Diese Ordnungsliste wirklich endgültig löschen?");
    if (!confirmed) return;

    setError("");
    const url = `${API_URL}/api/teacher/${id}/${secret}/delete`;
    try {
      const response = await fetch(url, { method: "POST" });
      if (response.ok) {
        setDeleted(true);
        setList(null);
        return;
      }

      const data = await readJsonResponse(response, "Liste löschen", url);
      setError(data.error || "Ordnungsliste konnte nicht gelöscht werden.");
    } catch (err) {
      setError(backendErrorMessage("Liste löschen", url, err));
    }
  }

  function printPdf() {
    window.print();
  }

  if (deleted) {
    return (
      <PageShell maxWidth="max-w-4xl">
        <div className="border border-[#dce8c8] bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold tracking-tight">Ordnungsliste gelöscht</h1>
          <p className="mt-4 text-base leading-7 text-neutral-600">Diese Ordnungsliste ist nicht mehr erreichbar.</p>
        </div>
      </PageShell>
    );
  }

  if (!list) {
    return (
      <PageShell maxWidth="max-w-4xl">
        <div className="border border-[#dce8c8] bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-semibold tracking-tight">Lehreransicht wird geladen...</h1>
          {error && <p className="mt-4 text-sm font-medium text-red-600">{error}</p>}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="max-w-4xl">
      <section className="mb-10 border-b border-[#dce8c8] pb-8 print:hidden">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.22em] text-neutral-500">KlasseOrdnung</p>
            <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">{list.title}</h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-neutral-600">
              Diese Ansicht aktualisiert sich automatisch, sobald sich Schüler eintragen.
            </p>
            <p className="mt-3 text-sm text-neutral-500">Status: {connectionStatus}</p>
          </div>

          <div className="flex flex-wrap gap-3">
            {!list.closed ? (
              <button className="border border-[#172017] bg-[#172017] px-6 py-3 text-sm font-medium text-white shadow-sm hover:bg-[#263826]" onClick={closeList}>
                Ordnungsliste schließen
              </button>
            ) : (
              <>
                <button className="border border-[#172017] bg-[#172017] px-6 py-3 text-sm font-medium text-white shadow-sm hover:bg-[#263826]" onClick={reopenList}>
                  Ordnungsliste wieder öffnen
                </button>
                <button className="border border-[#172017] bg-white px-6 py-3 text-sm font-medium text-[#172017] shadow-sm hover:bg-[#f1f5e8]" onClick={printPdf}>
                  Als PDF speichern
                </button>
              </>
            )}
            <button className="border border-red-300 bg-red-50 px-6 py-3 text-sm font-medium text-red-700 hover:bg-red-100" onClick={deleteList}>
              Liste löschen
            </button>
          </div>
        </div>
      </section>

      {error && <div className="mb-6 border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 print:hidden">{error}</div>}

      <section className="mb-8 border border-[#dce8c8] bg-white p-6 shadow-sm print:border-0 print:p-0 print:shadow-none">
        <h1 className="text-3xl font-semibold tracking-tight">{list.title}</h1>
        <p className="mt-2 text-sm text-neutral-500">Status: {list.closed ? "Geschlossen" : "Offen"}</p>
        <p className="mt-2 text-sm font-medium text-[#39501f]">
          Modus: {list.singleChoice ? "Jeder Name kann nur einen Eintrag belegen." : "Schüler können mehrere Einträge belegen."}
        </p>
      </section>

      <section className="space-y-4">
        {list.items.map(item => {
          const usedAmount = item.assignees.reduce((sum, person) => sum + Number(person.quantity || 1), 0);
          const full = usedAmount >= item.max_count;

          return (
            <article className="border border-[#dce8c8] bg-white p-5 shadow-sm print:break-inside-avoid print:shadow-none" key={item.id}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold tracking-tight">{item.name}</h2>
                  <p className="mt-1 text-sm text-neutral-500">{usedAmount} von {item.max_count} Plätzen vergeben</p>
                </div>
                <span className={full ? "w-fit border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700" : "w-fit border border-[#dce8c8] bg-[#eef5df] px-3 py-1 text-xs font-semibold text-[#39501f]"}>
                  {full ? "Voll" : "Offen"}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {item.assignees.length === 0 ? (
                  <p className="text-sm text-neutral-500">Noch niemand eingetragen.</p>
                ) : (
                  item.assignees.map(person => (
                    <span className="border border-[#dce8c8] bg-[#f8faf2] px-3 py-2 text-sm font-medium text-neutral-700" key={person.id}>
                      {person.name}
                    </span>
                  ))
                )}
              </div>
            </article>
          );
        })}
      </section>
    </PageShell>
  );
}

export default TeacherView;
